import { DateRange } from './../../dates/models/date-range.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { environment } from 'environments/environment';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpHelperService } from '../../forms/services/http-helper.service';
import {
  CasePhaseDropDownListItem,
  CasePhaseDropDownListItemJson,
} from '../models/case-phase-drop-down-list-item.model';
import { FormOutcomeVitalSign } from '../models/form-outcome-vital-sign.model';
import {
  ClientCaseDropDownListItem,
  ClientCaseDropDownListItemJson,
} from '../models/client-case-drop-down-list-item.model';
import { Client, ClientJson } from '../models/client.model';
import {
  EntityPaymentProfile,
  EntityPaymentProfileJson,
} from '../models/entity-payment-profile.model';
import { CombinedFormOutcomeTypesForClientCase } from '../models/combined-form-outcome-types-for-client-case.model';
import { WaitingList } from '../models/waiting-list.model';
import { PaymentIntegrator } from 'app/shared/clinics/models/payment-integrator.enum';
import {
  ClientInsuranceCoverage,
  ClientInsuranceCoverageListItem,
} from '../models/client-insurance-coverages.model';
import { DropdownItem } from 'app/shared/system/models/dropdown-item.model';

const bamboraLogos: { [key: string]: string } = {
  VI: 'https://cdn.na.bambora.com/downloads/images/cards/visa.svg',
  AM: 'https://cdn.na.bambora.com/downloads/images/cards/amex.svg',
  NN: 'https://cdn.na.bambora.com/downloads/images/cards/discover.svg',
  MC: 'https://cdn.na.bambora.com/downloads/images/cards/mastercard.svg',
};

const cloverLogos: { [key: string]: string } = {
  VISA: 'https://checkout.clover.com/assets/card-icons/visa.png',
  AMEX: 'https://checkout.clover.com/assets/card-icons/amex.png',
  DINERS_CLUB: 'https://checkout.clover.com/assets/card-icons/diners_club.png',
  JCB: 'https://checkout.clover.com/assets/card-icons/jcb.png',
  DISCOVER: 'https://checkout.clover.com/assets/card-icons/discover.png',
  MC: 'https://checkout.clover.com/assets/card-icons/mc.png',
};

@Injectable({
  providedIn: 'root',
})
export class ClientHttpService {
  private baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private httpHelperService: HttpHelperService
  ) {}

  getClient(clientId: number): Observable<Client> {
    const url = `${this.baseUrl}clients/${clientId}`;
    return this.http.get<ClientJson>(url).pipe(map(json => this.convertClientJsonToModel(json)));
  }

  getClientCaseDropDownListItems(clientId: number): Observable<ClientCaseDropDownListItem[]> {
    const url = `${this.baseUrl}clients/${clientId}/cases/dropdownlist`;
    return this.http
      .get<ClientCaseDropDownListItemJson[] | null>(url)
      .pipe(map(json => this.convertClientCasesJsonToModel(json)));
  }

  getClientPaymentProfiles(clientId: number): Observable<EntityPaymentProfile[]> {
    const url = `${this.baseUrl}entities/${clientId}/payments/profiles`;
    return this.http
      .get<EntityPaymentProfileJson[] | null>(url)
      .pipe(map(json => this.convertEntityPaymentProfileJsonToModels(json)));
  }

  createClientPaymentProfile(
    entityId: number,
    form: UntypedFormGroup
  ): Observable<EntityPaymentProfile> {
    const url = `${this.baseUrl}entities/${entityId}/payments/profiles`;
    return this.http
      .post<EntityPaymentProfileJson>(url, this.httpHelperService.createInsertBody(form))
      .pipe(map(json => this.convertEntityPaymentProfileJsonToModel(json)));
  }

  updateClientPaymentProfile(
    entityId: number,
    form: UntypedFormGroup
  ): Observable<EntityPaymentProfile> {
    const url = `${this.baseUrl}entities/${entityId}/payments/profiles/${form.get('id')?.value}`;
    return this.http
      .put<EntityPaymentProfileJson>(url, this.httpHelperService.createInsertBody(form))
      .pipe(map(json => this.convertEntityPaymentProfileJsonToModel(json)));
  }

  deleteClientPaymentProfile(entityId: number, profileId: number): Observable<any> {
    const url = `${this.baseUrl}entities/${entityId}/payments/profiles/${profileId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getActiveWaitingList(): Observable<WaitingList[] | null> {
    const url = `${this.baseUrl}waitinglists/?isActive=true`;
    return this.http.get<WaitingList[]>(url);
  }

  removeWaitingList(waitingListId: number): Observable<WaitingList> {
    const url = `${this.baseUrl}waitinglists/${waitingListId}`;

    const body: { op: string; path: string; value: false }[] = [];
    body.push({
      op: 'replace',
      path: 'isActive',
      value: false,
    });
    return this.http.patch<WaitingList>(url, body);
  }

  getAllClientChartNoteVitalSigns(dateRange: DateRange): Observable<FormOutcomeVitalSign[]> {
    const url =
      dateRange.from && dateRange.to
        ? `${this.baseUrl}clients/formoutcomevitalsigns?dateFrom=${moment(dateRange.from).format(
            'YYYY-MM-DDTHH:mm:ss'
          )}&dateTo=${moment(dateRange.to).format('YYYY-MM-DDTHH:mm:ss')}`
        : `${this.baseUrl}clients/formoutcomevitalsigns`;
    return this.http.get<FormOutcomeVitalSign[]>(url);
  }

  getClientSysFormOutcomeTypes(
    dateRange: DateRange
  ): Observable<CombinedFormOutcomeTypesForClientCase[]> {
    const url =
      dateRange.from && dateRange.to
        ? `${
            this.baseUrl
          }clientcaseformoutcomeresponses/client/sysformoutcometypes?dateFrom=${moment(
            dateRange.from
          ).format('YYYY-MM-DDTHH:mm:ss')}&dateTo=${moment(dateRange.to).format(
            'YYYY-MM-DDTHH:mm:ss'
          )}`
        : `${this.baseUrl}clientcaseformoutcomeresponses/client/sysformoutcometypes`;
    return this.http.get<CombinedFormOutcomeTypesForClientCase[]>(url);
  }

  getClientInsuranceCoverageListItems(): Observable<ClientInsuranceCoverageListItem[]> {
    const url = `${this.baseUrl}clients/clientinsurancecoverages`;
    return this.http.get<ClientInsuranceCoverageListItem[]>(url);
  }

  postClientInsuranceCoverage(form: UntypedFormGroup): Observable<ClientInsuranceCoverage> {
    const url = `${this.baseUrl}clients/clientinsurancecoverages`;
    return this.http.post<ClientInsuranceCoverage>(url, form.value);
  }

  patchClientInsuranceCoverage(
    insuranceFormGroup: UntypedFormGroup,
    clientInsuranceCoverageId: number
  ): Observable<ClientInsuranceCoverage> {
    const url = `${this.baseUrl}clients/clientinsurancecoverages/${clientInsuranceCoverageId}`;
    return this.http.patch<ClientInsuranceCoverage>(
      url,
      this.httpHelperService.createPatchBody(insuranceFormGroup)
    );
  }

  getClientInsuranceCoverage(
    clientInsuranceCoverageId: number
  ): Observable<ClientInsuranceCoverage> {
    const url = `${this.baseUrl}clients/clientinsurancecoverages/${clientInsuranceCoverageId}`;
    return this.http.get<ClientInsuranceCoverage>(url);
  }

  deleteClientInsuranceCoverages(clientInsuranceCoverageId: number): Observable<any> {
    const url = `${this.baseUrl}clients/clientinsurancecoverages/${clientInsuranceCoverageId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  getSysRelationshiptoInsureds(): Observable<DropdownItem[]> {
    const url = `${this.baseUrl}sysrelationshiptoinsureds/dropdownlist`;
    return this.http.get<DropdownItem[]>(url);
  }

  postClientInsurancecoverageAcceptationAudits(payload: any): Observable<any> {
    const url = `${this.baseUrl}clientinsurancecoverageacceptationaudits`;
    return this.http.post<any>(url, payload);
  }

  getMyReferrerCases(): Observable<any[]> {
    const url = `${this.baseUrl}cases/myreferrercases`;
    return this.http.get<any[]>(url);
  }

  private convertClientCasesJsonToModel(
    json: ClientCaseDropDownListItemJson[] | null
  ): ClientCaseDropDownListItem[] {
    if (json) {
      return json.map(item => this.convertClientCaseJsonToModel(item));
    } else {
      return [];
    }
  }

  private convertClientCaseJsonToModel(
    json: ClientCaseDropDownListItemJson
  ): ClientCaseDropDownListItem {
    return {
      id: json.id,
      name: json.name,
      defaultSupPractitionerId: json.defaultSupPractitionerId,
      showOnlyCaseServicesWhenBooking: json.showOnlyCaseServicesWhenBooking,
      casePhases: this.convertCasePhasesJsonToModel(json.casePhases),
      defaultSupPractitionerFullName: json.defaultSupPractitionerFullName,
      caseCode: json.caseCode,
      caseDisplayName: json.caseDisplayName,
      caseIntegrationType: json.caseIntegrationType,
      caseIntegrationTypeId: json.caseIntegrationTypeId,
      sysCaseIntegrationTypeId: json.sysCaseIntegrationTypeId,
      casePlanType: json.casePlanType,
      createDate: new Date(json.createDateUtc),
      isDischarged: json.isDischarged,
      totalOfAppointment: json.totalOfAppointment,
    };
  }

  private convertCasePhasesJsonToModel(
    json: CasePhaseDropDownListItemJson[] | null
  ): CasePhaseDropDownListItem[] {
    if (json) {
      return json.map(item => this.convertCasePhaseJsonToModel(item));
    } else {
      return [];
    }
  }

  private convertCasePhaseJsonToModel(
    json: CasePhaseDropDownListItemJson
  ): CasePhaseDropDownListItem {
    return {
      id: json.id,
      name: json.name,
      isActive: json.isActive,
      plannedEndDate: json.plannedEndDate ? new Date(json.plannedEndDate) : null,
      plannedNumberOfVisits: json.plannedNumberOfVisits,
      isCompleted: json.isCompleted,
      appointmentCount: json.appointmentCount,
    };
  }

  private convertClientJsonToModel(json: ClientJson): Client {
    return {
      clientId: json.clientId,
      clientFileId: json.clientFileId,
      dateOfBirth: json.dateOfBirth ? new Date(json.dateOfBirth) : undefined,
      initialContactDate: json.initialContactDate ? new Date(json.initialContactDate) : undefined,
      heightInCentimeters: json.heightInCentimeters,
      weightInKilograms: json.weightInKilograms,
      contactInformation: json.contactInformation,
      correspondanceLanguageId: json.sysCorrLanguageId,
      correspondanceLanguage: json.corrLanguage,
      entityId: json.entityId,
      firstName: json.firstName,
      fullName: json.fullName,
      lastName: json.lastName,
      maidenName: json.maidenName,
      middleName: json.middleName,
      entityTypeId: json.sysEntityTypeId,
      clientStatusId: json.sysClientStatusId,
      clientStatus: json.clientStatus,
      telephonySysPhoneTypeId: json.telephonySysPhoneTypeId,
      telephonySysPhoneType: json.telephonySysPhoneType,
      genderId: json.sysGenderId,
      gender: json.gender,
      legalSysGenderId: json.legalSysGenderId,
      legalSysGender: json.legalSysGender,
      maritalStatusId: json.sysMaritalStatusId,
      maritalStatus: json.maritalStatus,
      employerId: json.employerId,
      employer: json.employer,
      employmentStatusId: json.sysEmploymentStatusId,
      employmentStatus: json.sysEmploymentStatus,
      ethnicityId: json.ethnicityId,
      ethnicity: json.ethnicity,
      disabilityId: json.disabilityId,
      disability: json.disability,
      motherTongueId: json.motherTongueId,
      motherTongue: json.motherTongue,
      occupationId: json.occupationId,
      occupation: json.occupation,
      vipTypeId: json.vipTypeId,
      vipType: json.vipType,
      lastReferrerId: json.lastReferrerId,
      lastReferrerFullName: json.lastReferrerFullName,
      clientMedicareCard: json.clientMedicareCard,
      clientInfo: json.clientInfo,
      clientEmergencyContacts: json.clientEmergencyContacts,
      clientPreferredAppointmentNotificationTypes: json.clientPreferredAppointmentNotificationTypes,
      entityInterests: json.entityInterests,
      lastReferrer: json.lastReferrer,
      salutationId: json.sysSalutationId,
      salutation: json.salutation,
      webUserName: json.webUserName,
    };
  }

  private convertEntityPaymentProfileJsonToModels(
    json: EntityPaymentProfileJson[] | null
  ): EntityPaymentProfile[] {
    if (json) {
      return json.map(this.convertEntityPaymentProfileJsonToModel);
    } else {
      return [];
    }
  }

  private convertEntityPaymentProfileJsonToModel(
    json: EntityPaymentProfileJson
  ): EntityPaymentProfile {
    const brandKey =
      json.systemProfileDescription.substr(0, json.systemProfileDescription.indexOf(' ')) ||
      json.systemProfileDescription;

    return {
      id: json.id,
      entityId: json.entityId,
      sysPaymentIntegratorId: json.sysPaymentIntegratorId,
      profileNumber: json.profileNumber,
      systemProfileDescription: json.systemProfileDescription.substr(
        json.systemProfileDescription.indexOf(' ') + 1
      ),
      userProfileDescription: json.userProfileDescription,
      isDefault: json.isDefault,
      createDateUtc: new Date(json.createDateUtc),
      sysPaymentIntegrator: json.sysPaymentIntegrator,
      brand:
        json.sysPaymentIntegratorId === PaymentIntegrator.Bambora
          ? bamboraLogos[brandKey]
          : json.sysPaymentIntegratorId === PaymentIntegrator.CloverPaystreamCardNotPresent
            ? cloverLogos[brandKey]
            : '',
    };
  }
}
